import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { DataService } from '../service/data.service';
import { inject, Injector, runInInjectionContext } from '@angular/core';
import { AccessDeniedComponent } from '../component/dialog/access-denied/access-denied.component';
import { MatDialog } from '@angular/material/dialog';
import { LayoutService } from '../service/layout.service';
import { catchError, filter, map, of, take, timeout } from 'rxjs';

const PROFILE_SETTLE_TIMEOUT_MS = 15000;

export const canActivateByProfile: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const dataService = inject(DataService);
  const router = inject(Router);
  const failClosed = !!route.data['failClosedProfile'];
  const status$ = dataService.accessProfileStatus$;

  const deny = () => {
    console.warn('Perfil de acesso não pôde ser resolvido; navegação bloqueada');
    inject(LayoutService).loader = false;
    inject(MatDialog).open(AccessDeniedComponent);
    return false;
  };

  if (!route.data['profileRule'] || !status$ || router.url === '/company') {
    return _canActivateByProfileNow(route, state);
  }

  if (status$.value !== 'loading') {
    return failClosed && status$.value === 'error' ? deny() : _canActivateByProfileNow(route, state);
  }

  const injector = inject(Injector);
  return status$.pipe(
    filter(status => status !== 'loading'),
    take(1),
    timeout(PROFILE_SETTLE_TIMEOUT_MS),
    catchError(() => of('error' as const)),
    map(status => runInInjectionContext(injector, () =>
      failClosed && status === 'error' ? deny() : _canActivateByProfileNow(route, state)
    ) as boolean)
  );
};

const _canActivateByProfileNow: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const _dataService = inject(DataService);
  const _dialogService = inject(MatDialog);
  const _layout = inject(LayoutService);
  const _router = inject(Router);
  let expectedRule: string | string[] = route.data['profileRule'];
  if (!expectedRule || (Array.isArray(expectedRule) && expectedRule.length === 0)) {
    return true;
  }

  // Lista de chaves (ex.: painel de OS unificado): libera quando qualquer uma casar contra
  // module.tabs. O caminho de string única, abaixo, permanece idêntico ao comportamento anterior.
  if (Array.isArray(expectedRule)) {
    const expectedRules = expectedRule;

    if (_router.url === "/company") {
      // allow to exit company-list and enter into a company
      return true
    }

    if (!_dataService.accessProfile) {
      return true; // first paint
    }

    const blockedRule: string | undefined = route.data['blockedProfileRule'];
    if (blockedRule) {
      const blockedModuleName = blockedRule.split('.').shift();
      const blockedModule = _dataService.accessProfile.modules.find(m => m.name === blockedModuleName);
      if (blockedModule && blockedModule.tabs.some(tab => tab === blockedRule)) {
        console.warn('O usuário está bloqueado para esse componente', blockedRule);
        _layout.loader = false;
        _dialogService.open(AccessDeniedComponent);
        return false;
      }
    }

    const allowed = expectedRules.some(rule => {
      const moduleName = rule.split('.').shift();
      const module = _dataService.accessProfile.modules.find(m => m.name === moduleName);
      return !!module && module.tabs.some(tab => tab === rule);
    });

    if (allowed) {
      return true;
    }

    console.warn('O usuário não pode acessar esse componente', expectedRules);
    _layout.loader = false;
    _dialogService.open(AccessDeniedComponent);
    return false;
  }

  let moduleName = expectedRule.split('.').shift();
  const type = route.paramMap.get('type');
  if (type) {
    const profileModule = type === "transporter" ? "SUPPLIER" : type.toUpperCase();
    expectedRule = expectedRule.replace(moduleName, profileModule);
    // USER.REGISTERED_USERS
    if (type === "lead") {
      moduleName = "CRM"
      expectedRule = "CRM.leads.convertToClient"
    } else if (type === "user") {
      moduleName = type.toUpperCase();
      expectedRule = "USER.REGISTERED_USERS.update"
    } else {
      moduleName = profileModule;
    }
  }

  if (_router.url === "/company") {
    // allow to exit company-list and enter into a company
    return true
  }

  if (!_dataService.accessProfile) {
    return true; // first paint
  }

  // Regra de bloqueio (opt-out): se presente no perfil, nega o acesso mesmo com a regra positiva.
  // Ausente = liberado (default). Perfil null/master já retornou acima, então não é afetado.
  const blockedRule: string | undefined = route.data['blockedProfileRule'];
  if (blockedRule) {
    const blockedModuleName = blockedRule.split('.').shift();
    const blockedModule = _dataService.accessProfile.modules.find(m => m.name === blockedModuleName);
    if (blockedModule && blockedModule.tabs.some(tab => tab === blockedRule)) {
      console.warn('O usuário está bloqueado para esse componente', blockedRule);
      _layout.loader = false;
      _dialogService.open(AccessDeniedComponent);
      return false;
    }
  }

  if (_dataService.accessProfile) {
    const module = _dataService.accessProfile.modules.find(m => m.name === moduleName);

    if (module && module.tabs.some(tab => tab === expectedRule)) {
      return true;
    } else {
      // the profile no have access to requested route
      console.warn('O usuário não pode acessar esse componente', expectedRule);
      _layout.loader = false;
      _dialogService.open(AccessDeniedComponent);
      return false; // just dont navigate
    }
  }

  _dialogService.open(AccessDeniedComponent);
  console.warn('Navegação não permitida!', { profile: _dataService.accessProfile, route, state, moduleName, expectedRule, _router })
  _router.navigate(['company']); // user tryied to navigate typping on browser url, block!
  return false

}
